// src/pages/testimonies/testimonyDetails/testimonyDetails.js
import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input, Modal, Spin, Tag } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import { getCurrentUser } from "aws-amplify/auth";
import useNotification from "../../../hooks/useNotification";
import useFileViewer from "../../../hooks/useFileViewer";
import Layout from "../../../components/layout/layout";
import "./testimonyDetails.css";

const { TextArea } = Input;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  showModal: false,
  messageText: "",
  isSending: false,
  testimony: null,
  comments: [],
  notification: {
    show: false,
    message: "",
    type: "success"
  }
};

// Reducer for state management
const testimonyReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setTestimony":
      return { ...state, testimony: action.payload };
    case "setComments":
      return { ...state, comments: action.payload };
    case "addComment":
      return { ...state, comments: [...state.comments, action.payload] };
    case "setShowModal":
      return { ...state, showModal: action.payload };
    case "setMessageText":
      return { ...state, messageText: action.payload };
    case "setIsSending":
      return { ...state, isSending: action.payload };
    case "setNotification":
      return { ...state, notification: action.payload };
    case "resetMessageModal":
      return { ...state, showModal: false, messageText: "" };
    default:
      return state;
  }
};

function TestimonyDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(testimonyReducer, initialState);
  const { isLoading, showModal, messageText, isSending, testimony, comments } = state;
  const { handleViewFile: hookHandleViewFile, loadingFiles } = useFileViewer(testimony?.user_id);

  // Format date helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true, // or false if you want 24-hour format
    });
  }, []);
  
  // FIX: Fetch comments/communication history for this testimony
  const fetchComments = useCallback(async () => {
    if (!id) return;
    try {
      const token = await getAuthToken();
      const response = await axios.get(
        `${API_ENDPOINTS.TESTIMONIES}/${id}`,   // GET comments by testimony id
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("API RESPONSE:", response.data);
      // Normalise: accept array at root or nested under a key
      const data = response.data;
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.comments)
          ? data.comments
          : [];
      dispatch({ type: "setComments", payload: list });
    } catch (error) {
      console.error("Error fetching comments:", error);
      // Non-fatal — page still works without history
    }
  }, [id]);

  // Handle send message
  const handleSendMessage = useCallback(async () => {
  	if (isSending) {
      console.warn("Message is already being sent. Skipping this call.");
      return; // 🚨 prevents duplicate calls
    }

    if (!messageText.trim()) {
      showNotification("Please enter a message", "error");
      return;
    }

    dispatch({ type: "setIsSending", payload: true });

    try {
      const token = await getAuthToken();
      const currentUser = await getCurrentUser();
      const payload = {
        testimony_id: id,
        admin_id: currentUser.userId,
        message: messageText,
      };

      const response = await axios.post(
        API_ENDPOINTS.TESTIMONY_ADMIN_COMMENTS,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Message sent successfully:", response.data);

      // Update local comments state with the new message
      await fetchComments();

      showNotification("Message sent successfully!", "success");
      dispatch({ type: "resetMessageModal" });
    } catch (error) {
      console.error("Error sending message:", error);
      showNotification(error.response?.data?.message || "Failed to send message.", "error");
    } finally {
      dispatch({ type: "setIsSending", payload: false });
    }
  }, [messageText, id, showNotification, isSending, fetchComments]);

  // Handle modal cancel
  const handleModalCancel = useCallback(() => {
    dispatch({ type: "resetMessageModal" });
  }, []);

  // Handle modal open
  const handleOpenModal = useCallback(() => {
    dispatch({ type: "setShowModal", payload: true });
  }, []);

  // Handle message text change
  const handleMessageTextChange = useCallback((e) => {
    dispatch({ type: "setMessageText", payload: e.target.value });
  }, []);

  // Handle back navigation
  const handleBack = useCallback(() => {
    navigate("/testimonyList");
  }, [navigate]);

  const viewAttachment = useCallback(
    async (fileUrl, fileKey) => {
      if (!fileUrl) return;

      // Try viewing through signed URL first (same approach as other pages).
      let fileNameOnly = fileUrl;
      try {
        const parsed = new URL(String(fileUrl));
        fileNameOnly = decodeURIComponent(parsed.pathname.split("/").pop() || fileUrl);
      } catch {
        const parts = String(fileUrl).split("/");
        fileNameOnly = parts[parts.length - 1] || fileUrl;
      }

      const err = await hookHandleViewFile(fileNameOnly, fileKey, "testimony");
      if (err) {
        // Fallback to opening the raw URL if it is already publicly reachable
        if (typeof fileUrl === "string" && fileUrl.startsWith("http")) {
          window.open(fileUrl, "_blank", "noopener,noreferrer");
          return;
        }
        showNotification(err, "error", "File Viewer");
      }
    },
    [hookHandleViewFile, showNotification]
  );

  // Fetch testimony details
  useEffect(() => {
    let isMounted = true;

    const fetchTestimonyDetails = async () => {
      if (!id) return;

      dispatch({ type: "setLoading", payload: true });

      try {
        console.log("Fetching testimony for id:", id);
        const token = await getAuthToken();

        const response = await axios.get(`${API_ENDPOINTS.TESTIMONIES}/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (isMounted) {
          console.log("Testimony details response:", response.data);
          dispatch({ type: "setTestimony", payload: response.data });

          // Set form values
          form.setFieldsValue({
            title: response.data.title || "",
            description: response.data.description || "",
            submittedBy: response.data.name || "Unknown User",
            submittedOn: formatDate(response.data.created_at),
            visibility: response.data.is_public === "true" ? "Public" : "Private",
            status: response.data.status || "Submitted"
          });
        }
      } catch (error) {
        console.error("Error fetching testimony details:", error);
        if (isMounted) {
          showNotification(error.response?.data?.message || "Failed to load testimony details", "error");
        }
      } finally {
        if (isMounted) {
          dispatch({ type: "setLoading", payload: false });
        }
      }
    };

    fetchTestimonyDetails();
    fetchComments();

    return () => {
      isMounted = false;
    };
  }, [id, form, formatDate, showNotification, fetchComments]);

  // Memoize modal title
  const modalTitle = useMemo(() => `Send Message to ${testimony?.name || "User"}`, [testimony?.name]);

  // Memoize file items for rendering
  const fileItems = useMemo(() => {
    if (!testimony?.files || testimony.files.length === 0) return [];
    return testimony.files.map((file, index) => ({
      ...file,
      key: `file-${index}`,
      isImage: file.url?.match(/\.(jpg|jpeg|png|gif)$/i)
    }));
  }, [testimony?.files]);

  return (
    <Layout
      navTitle="Testimonies"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="vtd-form-container">
        <div className="vtd-form-wrapper">
          {isLoading ? (
            <div style={{ textAlign: "center", padding: "32px" }}>
              <Spin size="large" style={{ marginBottom: "10px" }} />
              <p>Loading testimony details...</p>
            </div>
          ) : !testimony ? (
            <div style={{ textAlign: "center", padding: "32px" }}>Testimony not found.</div>
          ) : (
            <Form form={form} layout="vertical" className="vtd-antd-form">
              {/* ── Title & Description ── */}
              <div className="vtd-form-section">
                <h3 className="vtd-section-title">Testimony Details</h3>
                <Form.Item label="Title" name="title">
                  <Input disabled />
                </Form.Item>
                <Form.Item label="Description" name="description">
                  <TextArea rows={4} disabled />
                </Form.Item>
              </div>

              {/* ── Attached Files ── */}
              {fileItems.length > 0 && (
                <div className="vtd-form-section">
                  <h3 className="vtd-section-title">Attached Files</h3>
                  <div className="vtd-files-section">
                    <div className="vtd-file-list">
                      {fileItems.map((file) => (
                        <div key={file.key} className="vtd-file-item">
                          <label className="vtd-file-label">{file.name || `File ${file.key}`}:</label>
                          {file.url ? (
                            <div className="vtd-file-content">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px"
                                }}
                              >
                                <span className="vtd-file-name">
                                  📄 {file.name || "Unnamed file"}
                                  {file.size_kb && ` (${file.size_kb} KB)`}
                                </span>
                                <Button className="vtd-file-item-btn" onClick={() => viewAttachment(file.url, file.key)} loading={loadingFiles[file.key]}>
                                  {loadingFiles[file.key] ? "Loading..." : "View File"}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <span className="vtd-no-document">No file uploaded.</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ── Audio ── */}
              {testimony.audio_url && (
                <div className="vtd-form-section">
                  <h3 className="vtd-section-title">Audio Recording</h3>
                  <audio controls className="vtd-audio-player" style={{ width: "100%" }}>
                    <source src={testimony.audio_url} type="audio/mpeg" />
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* ── Submission Info (read-only) ── */}
              <div className="vtd-form-section">
                <h3 className="vtd-section-title">Submission Information</h3>
                <div className="vtd-form-row">
                  <Form.Item label="Submitted By" name="submittedBy">
                    <Input disabled />
                  </Form.Item>
                  <Form.Item label="Submitted On" name="submittedOn">
                    <Input disabled />
                  </Form.Item>
                </div>
                <div className="vtd-form-row">
                  <Form.Item label="Visibility" name="visibility">
                    <Tag className="vtd-testimony-tags" color={testimony.is_public === "true" ? "green" : "red"}>
                      {testimony.is_public === "true" ? "Public" : "Private"}
                    </Tag>
                  </Form.Item>
                  <Form.Item label="Status" name="status">
                    <Tag className="vtd-testimony-tags" color="blue">
                      {testimony.status || "Submitted"}
                    </Tag>
                  </Form.Item>
                </div>
              </div>
              {/* FIX: Communication history section — renders all past messages */}
              <div className="vtd-form-section">
                <h3 className="vtd-section-title">Communication History</h3>
                {comments.length === 0 ? (
                  <p style={{ color: "#888", fontStyle: "italic" }}>
                    No messages have been sent yet.
                  </p>
                ) : (
                  <div className="vtd-comments-list">
                    {comments.map((comment, index) => (
                      <div key={comment.comment_id || index} className="vtd-comment-item">
                        <div className="vtd-comment-header">
                          <span className="vtd-comment-sender">
                            {comment.admin_name || "Admin"}
                          </span>
                          <span className="vtd-comment-date" style={{ marginLeft: "12px" }}>
                            {formatDate(comment.utc_timestamp)}
                          </span>
                        </div>

                        <p className="vtd-comment-message">
                          {comment.text}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Action Buttons ── */}
              <div className="vtd-form-btn-group">
                <Button className="vtd-form-cancel-btn" onClick={handleBack}>
                  Cancel
                </Button>
                <Button className="vtd-form-message-btn" onClick={handleOpenModal}>
                  Send Message
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>

      {/* ── Message Modal ── */}
      <Modal
        className="vtd-message-modal"
        title={modalTitle}
        open={showModal}
        onOk={handleSendMessage}
        onCancel={handleModalCancel}
        okText="Send Message"
        cancelText="Cancel"
        width={600}
        confirmLoading={isSending}
      >
        <TextArea rows={6} placeholder="Type your message here..." value={messageText} onChange={handleMessageTextChange} />
      </Modal>
    </Layout>
  );
}

export default TestimonyDetails;
